import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

const TAG_COLORS = {
  Incentives:  { bg: 'rgba(245,166,35,0.12)', color: '#b45309' },
  Technology:  { bg: 'rgba(99,102,241,0.12)', color: '#4338ca' },
  Savings:     { bg: 'rgba(34,197,94,0.12)',  color: '#15803d' },
  Permitting:  { bg: 'rgba(14,165,233,0.12)', color: '#0369a1' },
  Advisory:    { bg: 'rgba(168,85,247,0.12)', color: '#7e22ce' },
};

function ArticleBlock({ block, navigate }) {
  switch (block.type) {
    case 'intro':
      return (
        <p style={{ fontSize: '1.1rem', color: '#374151', lineHeight: 1.8, fontWeight: 500, borderLeft: '4px solid var(--gold)', paddingLeft: '1.25rem', marginBottom: '2rem' }}>
          {block.text}
        </p>
      );
    case 'heading':
      return (
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.3rem', fontWeight: 800, color: 'var(--navy)', marginTop: '2.5rem', marginBottom: '0.75rem' }}>
          {block.text}
        </h2>
      );
    case 'paragraph':
      return (
        <p style={{ fontSize: '1rem', color: '#4b5563', lineHeight: 1.85, marginBottom: '1.25rem' }}>
          {block.text}
        </p>
      );
    case 'table':
      return (
        <div style={{ overflowX: 'auto', marginBottom: '1.75rem', borderRadius: 12, border: '1px solid var(--gray-200)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ background: 'var(--navy)' }}>
                {block.headers.map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', color: '#fff', fontFamily: 'Sora, sans-serif', fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)' }}>
                  {(row.cells || row).map((cell, j) => (
                    <td key={j} style={{ padding: '0.7rem 1rem', color: j === 0 ? 'var(--navy)' : '#4b5563', fontWeight: j === 0 ? 600 : 400, lineHeight: 1.5 }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'cta':
      return (
        <div style={{ background: 'linear-gradient(135deg, var(--navy), #2d4a8a)', borderRadius: 16, padding: '2rem', marginTop: '2.5rem', marginBottom: '1rem', textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '1.25rem' }}>{block.text}</p>
          <button onClick={() => navigate(block.to)}
            style={{ background: 'var(--gold)', color: 'var(--navy)', border: 'none', padding: '0.85rem 2rem', borderRadius: 50, fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', boxShadow: '0 4px 20px rgba(245,166,35,0.35)', transition: 'transform 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
            {block.button}
          </button>
        </div>
      );
    default:
      return null;
  }
}

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(undefined); // undefined = loading, null = not found
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(undefined);

    async function load() {
      try {
        const q = query(
          collection(db, 'posts'),
          where('slug', '==', slug),
          where('published', '==', true),
          limit(1)
        );
        const snap = await getDocs(q);
        if (snap.empty) {
          setArticle(null);
          return;
        }
        setArticle(snap.docs[0].data());

        // Related articles: latest published posts, excluding this one
        const relQ = query(
          collection(db, 'posts'),
          where('published', '==', true),
          orderBy('createdAt', 'desc'),
          limit(4)
        );
        const relSnap = await getDocs(relQ);
        const relItems = relSnap.docs
          .map(d => d.data())
          .filter(a => a.slug !== slug)
          .slice(0, 2);
        setRelated(relItems);
      } catch (e) {
        console.error('Failed to load article:', e);
        setArticle(null);
      }
    }
    load();
  }, [slug]);

  if (article === undefined) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: 'Sora', color: 'var(--navy)' }}>Loading…</p>
      </div>
    );
  }

  if (!article) {
    return (
      <>
  
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
          <h2 style={{ fontFamily: 'Sora', color: 'var(--navy)' }}>Article not found</h2>
          <button onClick={() => navigate('/')} style={{ background: 'var(--gold)', color: 'var(--navy)', border: 'none', padding: '0.75rem 1.75rem', borderRadius: 50, fontFamily: 'Sora', fontWeight: 700, cursor: 'pointer' }}>← Back to Home</button>
        </div>
  
      </>
    );
  }

  const tagStyle = TAG_COLORS[article.tag] || TAG_COLORS.Savings;

  return (
    <>
      <Helmet>
        <title>{article.title} | SolarSavings</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={article.img} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://solarsavings.com/blog/${article.slug}`} />
      </Helmet>



      {/* Hero */}
      <div style={{ position: 'relative', height: 420, overflow: 'hidden' }}>
        <img src={article.img} alt={article.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,20,55,0.85) 0%, rgba(10,20,55,0.3) 60%, transparent 100%)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '2.5rem', maxWidth: 800, margin: '0 auto' }}>
          <span style={{ display: 'inline-block', padding: '0.25rem 0.85rem', borderRadius: 50, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: tagStyle.bg, color: tagStyle.color, backdropFilter: 'blur(8px)', marginBottom: '0.75rem' }}>
            {article.tag}
          </span>
          <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.5rem, 3.5vw, 2.2rem)', fontWeight: 800, color: '#fff', lineHeight: 1.2, margin: 0 }}>
            {article.title}
          </h1>
        </div>
      </div>

      {/* Breadcrumb + date */}
      <div style={{ background: '#fff', borderBottom: '1px solid var(--gray-100)', padding: '0.75rem 2.5rem' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--gray-400)' }}>
          <Link to="/" style={{ color: 'var(--gold)', textDecoration: 'none', fontWeight: 600 }}>Home</Link>
          <span>›</span>
          <span>Insights</span>
          <span>›</span>
          <span style={{ color: 'var(--navy)', fontWeight: 500 }}>{article.title}</span>
          <span style={{ marginLeft: 'auto' }}>{article.date}</span>
        </div>
      </div>

      {/* Content */}
      <section style={{ padding: '3rem 2.5rem 5rem', background: 'var(--gray-50)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            style={{ background: '#fff', borderRadius: 20, padding: 'clamp(1.5rem, 4vw, 3rem)', boxShadow: '0 8px 40px rgba(26,43,95,0.08)', border: '1px solid var(--gray-100)' }}>
            {article.content.map((block, i) => (
              <ArticleBlock key={i} block={block} navigate={navigate} />
            ))}
          </motion.div>

          {/* Related articles */}
          {related.length > 0 && (
            <div style={{ marginTop: '3rem' }}>
              <h3 style={{ fontFamily: 'Sora', fontWeight: 800, color: 'var(--navy)', fontSize: '1.1rem', marginBottom: '1.25rem' }}>Related Articles</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                {related.map(rel => {
                  const rt = TAG_COLORS[rel.tag] || TAG_COLORS.Savings;
                  return (
                    <Link key={rel.slug} to={`/blog/${rel.slug}`} style={{ textDecoration: 'none' }}
                      onClick={() => window.scrollTo(0, 0)}>
                      <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(26,43,95,0.08)', border: '1px solid var(--gray-100)', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer' }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(26,43,95,0.14)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(26,43,95,0.08)'; }}>
                        <div style={{ height: 140, overflow: 'hidden' }}>
                          <img src={rel.img} alt={rel.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div style={{ padding: '1rem 1.25rem' }}>
                          <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: rt.color }}>{rel.tag}</span>
                          <p style={{ fontFamily: 'Sora', fontWeight: 700, color: 'var(--navy)', fontSize: '0.9rem', lineHeight: 1.4, margin: '0.4rem 0 0.5rem' }}>{rel.title}</p>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gold)' }}>Read More →</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>


    </>
  );
}
